import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  FlaskConical,
  FileText,
  DollarSign,
  Clock,
  MapPin,
  Users,
  Wifi,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Check,
  Sparkles,
  Wand2,
  Plus,
  X,
  Award,
  Loader2,
} from 'lucide-react';
import { STUDY_CATEGORIES, UCI_MAJORS, ETHNICITIES } from '@apex/shared';
import { api } from '@/lib/api';

const stepVariant = {
  enter: (dir: number) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] } },
  exit: (dir: number) => ({ x: dir > 0 ? -80 : 80, opacity: 0, transition: { duration: 0.25 } }),
};

const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

const YEARS = [
  { value: 'freshman', label: 'Freshman' },
  { value: 'sophomore', label: 'Sophomore' },
  { value: 'junior', label: 'Junior' },
  { value: 'senior', label: 'Senior' },
  { value: 'graduate', label: 'Graduate' },
  { value: 'postdoc', label: 'Postdoc' },
] as const;

const COMP_TYPES = [
  { value: 'cash', label: 'Cash' },
  { value: 'credit', label: 'Course Credit' },
  { value: 'gift_card', label: 'Gift Card' },
  { value: 'other', label: 'Other' },
] as const;

interface SlotInput {
  date: string;
  start_time: string;
  end_time: string;
  capacity: number;
}

export default function CreateStudy() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const totalSteps = 4;

  /* Step 1: Basic Info */
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);

  /* Step 2: Details */
  const [compensationAmount, setCompensationAmount] = useState<number | ''>('');
  const [compensationType, setCompensationType] = useState('cash');
  const [durationMinutes, setDurationMinutes] = useState<number | ''>('');
  const [isRemote, setIsRemote] = useState(false);
  const [location, setLocation] = useState('');
  const [maxParticipants, setMaxParticipants] = useState<number | ''>(20);

  /* Step 3: Criteria */
  const [minAge, setMinAge] = useState<number | ''>('');
  const [maxAge, setMaxAge] = useState<number | ''>('');
  const [genders, setGenders] = useState<string[]>([]);
  const [majors, setMajors] = useState<string[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [minReliabilityScore, setMinReliabilityScore] = useState<number | ''>(0);
  const [aiSuggesting, setAiSuggesting] = useState(false);

  /* Step 4: Slots */
  const [slots, setSlots] = useState<SlotInput[]>([{ date: '', start_time: '', end_time: '', capacity: 5 }]);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const goNext = () => { setDir(1); setStep((s) => Math.min(s + 1, totalSteps - 1)); };
  const goPrev = () => { setDir(-1); setStep((s) => Math.max(s - 1, 0)); };

  /* AI: generate description */
  const handleAiDescription = async () => {
    if (!title) { toast.error('Enter a title first'); return; }
    setAiGenerating(true);
    try {
      const result = await api.generateDescription({
        title,
        notes: description || 'No additional notes',
        category: category || undefined,
      });
      if (result.ai_unavailable) {
        toast('AI is not available in this environment', { icon: '🤖' });
      } else {
        setDescription(result.description);
        toast.success('Description generated!');
      }
    } catch (err: any) {
      toast.error(err.message || 'AI generation failed');
    } finally {
      setAiGenerating(false);
    }
  };

  /* AI: suggest criteria */
  const handleAiCriteria = async () => {
    if (!title || !description) { toast.error('Fill in title and description first'); return; }
    setAiSuggesting(true);
    try {
      const result = await api.suggestCriteria({ title, description });
      if (result.ai_unavailable) {
        toast('AI is not available in this environment', { icon: '🤖' });
      } else {
        const c = result.criteria || {};
        if (c.min_age) setMinAge(c.min_age);
        if (c.max_age) setMaxAge(c.max_age);
        if (c.genders?.length) setGenders(c.genders);
        if (c.majors?.length) setMajors(c.majors);
        if (c.years?.length) setYears(c.years);
        if (c.min_reliability_score) setMinReliabilityScore(c.min_reliability_score);
        toast.success('Criteria suggested!');
      }
    } catch (err: any) {
      toast.error(err.message || 'AI suggestion failed');
    } finally {
      setAiSuggesting(false);
    }
  };

  /* Add/remove slots */
  const addSlot = () => setSlots((s) => [...s, { date: '', start_time: '', end_time: '', capacity: 5 }]);
  const removeSlot = (i: number) => setSlots((s) => s.filter((_, idx) => idx !== i));
  const updateSlot = (i: number, key: keyof SlotInput, value: string | number) => {
    setSlots((s) => s.map((slot, idx) => idx === i ? { ...slot, [key]: value } : slot));
  };

  /* Submit */
  const handleSubmit = async () => {
    if (!title) { toast.error('Title is required'); return; }
    if (!description) { toast.error('Description is required'); return; }
    if (!compensationAmount) { toast.error('Compensation amount is required'); return; }
    if (!durationMinutes) { toast.error('Duration is required'); return; }
    if (!startsAt || !endsAt) { toast.error('Study start and end dates are required'); return; }

    setIsSubmitting(true);
    try {
      const criteria: Record<string, unknown> = {};
      if (minAge) criteria.min_age = Number(minAge);
      if (maxAge) criteria.max_age = Number(maxAge);
      if (genders.length) criteria.genders = genders;
      if (majors.length) criteria.majors = majors;
      if (years.length) criteria.years = years;
      if (minReliabilityScore) criteria.min_reliability_score = Number(minReliabilityScore);

      const study = await api.createStudy({
        title,
        description,
        compensation_amount: Number(compensationAmount),
        compensation_type: compensationType,
        duration_minutes: Number(durationMinutes),
        is_remote: isRemote,
        location: isRemote ? null : location || null,
        max_participants: Number(maxParticipants) || 20,
        category: category || null,
        criteria,
        min_reliability_score: Number(minReliabilityScore) || 0,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
      });

      // Create slots
      const validSlots = slots.filter((s) => s.date && s.start_time && s.end_time);
      if (validSlots.length > 0) {
        const formattedSlots = validSlots.map((s) => ({
          start_time: new Date(`${s.date}T${s.start_time}`).toISOString(),
          end_time: new Date(`${s.date}T${s.end_time}`).toISOString(),
          capacity: s.capacity || 5,
        }));
        await api.createSlots(study.id, { slots: formattedSlots });
      }

      toast.success('Study created! You can activate it from the manage page.');
      navigate(`/my-studies/${study.id}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create study');
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepLabels = ['Basics', 'Details', 'Criteria', 'Schedule'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-3xl mx-auto"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500 to-accent-500 shadow-lg shadow-brand-500/25 mb-4">
          <FlaskConical className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight">Create a Study</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Set up your research study and find participants
        </p>
      </div>

      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                  i < step
                    ? 'bg-brand-500 text-white shadow-md shadow-brand-500/30'
                    : i === step
                    ? 'bg-gradient-to-br from-brand-500 to-accent-500 text-white shadow-lg shadow-brand-500/30 scale-110'
                    : 'bg-gray-200 dark:bg-gray-800 text-gray-400'
                }`}
              >
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              {i < totalSteps - 1 && (
                <div className="hidden sm:block w-12 lg:w-20 h-1 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
                  <motion.div
                    className="h-full bg-brand-500 rounded-full"
                    initial={false}
                    animate={{ width: i < step ? '100%' : '0%' }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
          {stepLabels.map((label) => (
            <span key={label}>{label}</span>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="card p-6 sm:p-8 overflow-hidden min-h-[400px] relative">
        <AnimatePresence mode="wait" custom={dir}>
          {/* Step 0: Basic Info */}
          {step === 0 && (
            <motion.div key="step0" variants={stepVariant} custom={dir} initial="enter" animate="center" exit="exit" className="space-y-5">
              <h3 className="text-lg font-semibold">Study Details</h3>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Title *</label>
                <input
                  type="text"
                  placeholder="e.g. Visual Perception Study on Color Recognition"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input-field"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="input-field">
                  <option value="">Select category</option>
                  {STUDY_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description *</label>
                  <button
                    onClick={handleAiDescription}
                    disabled={aiGenerating}
                    className="flex items-center gap-1.5 text-xs font-medium text-accent-500 hover:text-accent-600 transition-colors"
                  >
                    {aiGenerating ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Wand2 className="w-3.5 h-3.5" />
                    )}
                    AI Generate
                  </button>
                </div>
                <textarea
                  rows={5}
                  placeholder="Describe your study, what participants will do, and what they should expect..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="input-field resize-none"
                />
              </div>
            </motion.div>
          )}

          {/* Step 1: Details */}
          {step === 1 && (
            <motion.div key="step1" variants={stepVariant} custom={dir} initial="enter" animate="center" exit="exit" className="space-y-5">
              <h3 className="text-lg font-semibold">Study Parameters</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Compensation ($) *</label>
                  <div className="relative">
                    <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      placeholder="25"
                      value={compensationAmount}
                      onChange={(e) => setCompensationAmount(e.target.value ? parseFloat(e.target.value) : '')}
                      className="input-field pl-11"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Compensation Type</label>
                  <select value={compensationType} onChange={(e) => setCompensationType(e.target.value)} className="input-field">
                    {COMP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Duration (minutes) *</label>
                  <div className="relative">
                    <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="number"
                      min={5}
                      placeholder="60"
                      value={durationMinutes}
                      onChange={(e) => setDurationMinutes(e.target.value ? parseInt(e.target.value) : '')}
                      className="input-field pl-11"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Max Participants</label>
                  <div className="relative">
                    <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="number"
                      min={1}
                      placeholder="20"
                      value={maxParticipants}
                      onChange={(e) => setMaxParticipants(e.target.value ? parseInt(e.target.value) : '')}
                      className="input-field pl-11"
                    />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Location Type</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setIsRemote(false)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        !isRemote
                          ? 'bg-brand-500/10 border-brand-500/40 text-brand-600 dark:text-brand-400'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500'
                      }`}
                    >
                      <MapPin className="w-3 h-3 inline mr-1" /> In-Person
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsRemote(true)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        isRemote
                          ? 'bg-accent-500/10 border-accent-500/40 text-accent-600 dark:text-accent-400'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500'
                      }`}
                    >
                      <Wifi className="w-3 h-3 inline mr-1" /> Remote
                    </button>
                  </div>
                </div>
                {!isRemote && (
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="e.g. SBSG 2201, UCI campus"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className="input-field pl-11"
                    />
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 2: Criteria */}
          {step === 2 && (
            <motion.div key="step2" variants={stepVariant} custom={dir} initial="enter" animate="center" exit="exit" className="space-y-5">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Participant Criteria</h3>
                <button
                  onClick={handleAiCriteria}
                  disabled={aiSuggesting}
                  className="flex items-center gap-1.5 text-xs font-medium text-accent-500 hover:text-accent-600 transition-colors"
                >
                  {aiSuggesting ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Wand2 className="w-3.5 h-3.5" />
                  )}
                  AI Suggest
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Optional. Leave blank to accept all participants.</p>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Min Age</label>
                  <input type="number" min={16} value={minAge} onChange={(e) => setMinAge(e.target.value ? parseInt(e.target.value) : '')} className="input-field" placeholder="18" />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Max Age</label>
                  <input type="number" max={100} value={maxAge} onChange={(e) => setMaxAge(e.target.value ? parseInt(e.target.value) : '')} className="input-field" placeholder="65" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Min Reliability Score</label>
                <div className="relative">
                  <Award className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input type="number" min={0} max={5} step={0.5} value={minReliabilityScore} onChange={(e) => setMinReliabilityScore(e.target.value ? parseFloat(e.target.value) : '')} className="input-field pl-11" placeholder="0" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Accepted Genders</label>
                <div className="flex flex-wrap gap-2">
                  {GENDERS.map((g) => (
                    <button
                      key={g.value}
                      type="button"
                      onClick={() => setGenders((prev) => prev.includes(g.value) ? prev.filter((x) => x !== g.value) : [...prev, g.value])}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        genders.includes(g.value)
                          ? 'bg-brand-500/10 border-brand-500/40 text-brand-600 dark:text-brand-400'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500'
                      }`}
                    >
                      {genders.includes(g.value) && <Check className="w-3 h-3 inline mr-1" />}
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Accepted Academic Years</label>
                <div className="flex flex-wrap gap-2">
                  {YEARS.map((y) => (
                    <button
                      key={y.value}
                      type="button"
                      onClick={() => setYears((prev) => prev.includes(y.value) ? prev.filter((x) => x !== y.value) : [...prev, y.value])}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        years.includes(y.value)
                          ? 'bg-brand-500/10 border-brand-500/40 text-brand-600 dark:text-brand-400'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500'
                      }`}
                    >
                      {years.includes(y.value) && <Check className="w-3 h-3 inline mr-1" />}
                      {y.label}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Schedule & Slots */}
          {step === 3 && (
            <motion.div key="step3" variants={stepVariant} custom={dir} initial="enter" animate="center" exit="exit" className="space-y-5">
              <h3 className="text-lg font-semibold">Schedule & Time Slots</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Study Start Date *</label>
                  <input type="date" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className="input-field" />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Study End Date *</label>
                  <input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className="input-field" />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Time Slots</label>
                  <button type="button" onClick={addSlot} className="text-xs font-medium text-brand-500 hover:text-brand-600 flex items-center gap-1">
                    <Plus className="w-3.5 h-3.5" /> Add Slot
                  </button>
                </div>

                {slots.map((slot, i) => (
                  <div key={i} className="flex items-end gap-2 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800">
                    <div className="flex-1 space-y-1">
                      <label className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Date</label>
                      <input type="date" value={slot.date} onChange={(e) => updateSlot(i, 'date', e.target.value)} className="input-field text-sm py-2" />
                    </div>
                    <div className="flex-1 space-y-1">
                      <label className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Start</label>
                      <input type="time" value={slot.start_time} onChange={(e) => updateSlot(i, 'start_time', e.target.value)} className="input-field text-sm py-2" />
                    </div>
                    <div className="flex-1 space-y-1">
                      <label className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">End</label>
                      <input type="time" value={slot.end_time} onChange={(e) => updateSlot(i, 'end_time', e.target.value)} className="input-field text-sm py-2" />
                    </div>
                    <div className="w-20 space-y-1">
                      <label className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Capacity</label>
                      <input type="number" min={1} value={slot.capacity} onChange={(e) => updateSlot(i, 'capacity', parseInt(e.target.value) || 1)} className="input-field text-sm py-2" />
                    </div>
                    {slots.length > 1 && (
                      <button type="button" onClick={() => removeSlot(i)} className="p-2 text-gray-400 hover:text-danger-500 transition-colors">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100 dark:border-gray-800">
          <button type="button" onClick={goPrev} disabled={step === 0} className="btn-ghost disabled:opacity-0">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>

          {step < totalSteps - 1 ? (
            <button type="button" onClick={goNext} className="btn-primary">
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="btn-primary"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating...
                </div>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Create Study
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
